import type { ClientMessage, GameMessage, LobbyMessage, ServerMessage } from "@coup/shared";
import { create } from "zustand";
import { clearToken, getToken, setToken } from "./session";

interface ErrorNotice {
  code: string;
  message: string;
  at: number;
}

interface State {
  connected: boolean;
  /** The lobby this tab is seated in. */
  code?: string;
  you?: string;
  lobby?: LobbyMessage;
  game?: GameMessage;
  /** serverNow − local now, for drawing server deadlines. */
  clockOffset: number;
  error?: ErrorNotice;
  send: (msg: ClientMessage) => void;
  /** Reclaims the seat for this code with a stored token. Returns false if there is none. */
  rejoin: (code: string) => boolean;
  leave: () => void;
  clearError: () => void;
}

let socket: WebSocket | undefined;
let queue: ClientMessage[] = [];
let retryMs = 500;
let stopped = false;

export const useStore = create<State>((set, get) => ({
  connected: false,
  clockOffset: 0,
  send: (msg) => {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
    else queue.push(msg);
  },
  rejoin: (code) => {
    const token = getToken(code);
    if (!token) return false;
    if (get().code !== code) get().send({ t: "join", code, token });
    return true;
  },
  leave: () => {
    const { code, send } = get();
    send({ t: "leave" });
    if (code) clearToken(code);
    set({ code: undefined, you: undefined, lobby: undefined, game: undefined });
  },
  clearError: () => set({ error: undefined }),
}));

function onMessage(msg: ServerMessage) {
  if (import.meta.env.DEV) console.debug("[ws]", msg.t, msg);
  const { code } = useStore.getState();
  switch (msg.t) {
    case "joined":
      setToken(msg.code, msg.token);
      useStore.setState({
        code: msg.code,
        you: msg.you,
        ...(msg.code !== code && { game: undefined, lobby: undefined }),
      });
      return;
    case "lobby":
      if (msg.code !== code) return;
      useStore.setState({ lobby: msg, ...(msg.phase === "lobby" && { game: undefined }) });
      return;
    case "game":
      useStore.setState({ game: msg, clockOffset: msg.serverNow - Date.now() });
      return;
    case "error":
      useStore.setState({ error: { code: msg.code, message: msg.message, at: Date.now() } });
      if (msg.code === "kicked" || msg.code === "session" || msg.code === "replaced") {
        if (code && msg.code !== "replaced") clearToken(code);
        useStore.setState({ code: undefined, you: undefined, lobby: undefined, game: undefined });
      }
      if (msg.code === "replaced") stopped = true;
      return;
  }
}

function connect() {
  const ws = new WebSocket(
    `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`,
  );
  socket = ws;
  ws.onopen = () => {
    retryMs = 500;
    useStore.setState({ connected: true });
    // Reclaim the seat after a drop, then flush anything sent while offline.
    const { code } = useStore.getState();
    const token = code && getToken(code);
    if (code && token) ws.send(JSON.stringify({ t: "join", code, token }));
    for (const msg of queue) ws.send(JSON.stringify(msg));
    queue = [];
  };
  ws.onmessage = (e) => onMessage(JSON.parse(e.data));
  ws.onclose = () => {
    useStore.setState({ connected: false });
    if (stopped) return;
    setTimeout(connect, retryMs);
    retryMs = Math.min(retryMs * 2, 8000);
  };
}

connect();
